import DomManager from "@scripts/core/DomManager.js";
import Mediator from "@scripts/core/Mediator.js";
import ProductQuantityManager from "@scripts/components/product/ProductQuantityManager.js";

export default class CartEventManager {
  constructor(cartManager) {
    this.cartManager = cartManager;
    this.addToCartButtons = [];
    this.removeFromCartButtons = [];
    this.productCards = [];
    this.addToCartListeners = new Map();
    this.removeFromCartListeners = new Map();
    this.productQuantityManagers = [];
  }

  init() {
    this.#getDomElements();
    this.#handleDomEvents();
    this.#initProductQuantityManagers();

    Mediator.subscribe("mini_cart_updated", () => {
      this.#updateDOMEvents();
    });
  }

  #shouldInitQuantityManager() {
    // Only initialize on archive pages and home page, NOT on single product pages
    const body = document.body;

    // Check if we're on a single product page
    if (body.classList.contains('single-product')) {
      return false;
    }

    // Check if we're on an archive page or home page
    const isArchive = body.classList.contains('archive') ||
                     body.classList.contains('post-type-archive-product') ||
                     body.classList.contains('woocommerce-shop') ||
                     body.classList.contains('home') ||
                     body.classList.contains('blog');

    return isArchive;
  }

  #getDomElements() {
    this.addToCartButtons = document.querySelectorAll(
      "[data-ajax-add-to-cart]",
    );
    this.removeFromCartButtons = document.querySelectorAll(
      "[data-remove-from-cart]",
    );
    this.productCards = document.querySelectorAll(".product-card");
  }

  #initProductQuantityManagers() {
    // Don't initialize on single product pages
    if (!this.#shouldInitQuantityManager()) {
      return;
    }

    // Destroy existing managers
    this.productQuantityManagers.forEach((manager) => manager.destroy());
    this.productQuantityManagers = [];

    // Initialize new managers for each product card
    this.productCards.forEach((productCard) => {
      const productId = productCard.querySelector("[data-ajax-add-to-cart]")?.dataset?.ajaxAddToCart;

      if (productId) {
        productCard.dataset.productId = productId;
        const manager = new ProductQuantityManager(productCard);
        this.productQuantityManagers.push(manager);
      }
    });
  }

  #handleDomEvents() {
    this.addToCartButtons.forEach((button) => {
      const addToCartListener = (e) => {
        e.preventDefault();
        this.cartManager.addToCart(button);
      };
      button.addEventListener("click", addToCartListener);
      this.addToCartListeners.set(button, addToCartListener);
    });

    this.removeFromCartButtons.forEach((button) => {
      const removeFromCartListener = (e) => {
        e.preventDefault();
        this.cartManager.removeFromCart(button);
      };
      button.addEventListener("click", removeFromCartListener);
      this.removeFromCartListeners.set(button, removeFromCartListener);
    });
  }

  #updateDOMEvents() {
    this.#removeDomEvents();
    this.#getDomElements();
    this.#handleDomEvents();
    this.#initProductQuantityManagers();
  }

  #removeDomEvents() {
    this.addToCartListeners.forEach((listener, button) => {
      button.removeEventListener("click", listener);
    });
    this.addToCartListeners.clear();

    this.removeFromCartListeners.forEach((listener, button) => {
      button.removeEventListener("click", listener);
    });
    this.removeFromCartListeners.clear();

    // Destroy product quantity managers
    this.productQuantityManagers.forEach((manager) => manager.destroy());
    this.productQuantityManagers = [];
  }
}
